import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, getStorageUrl } from '../services/api';
import authService from '../services/authService';
import SearchBar from './SearchBar';
import useSEO from '../hooks/useSEO';
import { courseUrl } from '../utils/catalogUrls';
import '../styles/ListaCurso.css';
import '../styles/SearchBar.css';

const testimonials = [
  {
    text: 'Gente! Melhor plataforma. Fiz meus cursos rapidinho e consegui um novo emprego. Super indico!',
    name: 'Carla Montovão',
    photo: '/testimonial-carla.png',
  },
  {
    text: 'Precisava de curso rápido para subir de cargo na minha empresa. Fiz 5 cursos e meu chefe já promoveu. Sou muito grata à Abraliber.',
    name: 'Marcos Antônio',
    photo: '/testimonial-marcos.jpg',
  },
  {
    text: 'Descobri a Abraliber neste ano e me apaixonei! Gosto muito de estudar Direito, há aulas de diversos deles. O curso é rápido e o material é organizado e muito bom. Peguei meu certificado no mesmo dia.',
    name: 'Manuela Freitas',
    photo: '/testimonial-manuela.jpg',
  },
];

const ListaCurso = () => {
  const [cursos, setCursos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalCursos, setTotalCursos] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const CURSOS_PER_PAGE = 12;
  const navigate = useNavigate();

  useSEO({
    title: 'ABRALIBER | Cursos Online Gratuitos com Certificado',
    description: 'A ABRALIBER oferece mais de 400 cursos online gratuitos. Estude gratuitamente e, após a conclusão, solicite seu certificado digital com a opção de criar um currículo profissional.',
    url: '/',
    keywords: 'ABRALIBER, cursos grátis, curso gratuito online, curso com certificado, cursos extracurriculares, melhorar currículo, curso EAD gratuito, curso livre online, capacitação gratuita',
  });

  useEffect(() => {
    fetchCursos(1, true);
    fetchCategorias();
  }, []);

  useEffect(() => {
    fetchCursos(1, true);
  }, [activeCategory]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 2);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const fetchCursos = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const params = { page, per_page: CURSOS_PER_PAGE };
      if (activeCategory) params.categoria = activeCategory.id;
      const response = await apiService.cursos.list(params);
      const data = response.data;
      const cursosData = data.data || [];
      if (reset) {
        setCursos(Array.isArray(cursosData) ? cursosData : []);
      } else {
        setCursos(prev => [...prev, ...(Array.isArray(cursosData) ? cursosData : [])]);
      }
      setCurrentPage(data.current_page || 1);
      setLastPage(data.last_page || 1);
      setTotalCursos(data.total || 0);
    } catch (error) {

    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (currentPage < lastPage && !loadingMore) {
      fetchCursos(currentPage + 1, false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await apiService.categorias.list();
      setCategorias(response.data.data || []);
    } catch (error) {

    }
  };

  const handleFilter = (categoria) => {
    setActiveCategory(categoria);
  };

  const handleChipClick = (cat) => {
    if (activeCategory && activeCategory.id === cat.id) {
      setActiveCategory(null);
    } else {
      setActiveCategory(cat);
    }
  };

  const handleMatriculaGratis = async (curso) => {
    const user = authService.getCurrentUser();
    if (!user) {
      navigate(courseUrl(curso));
    } else {
      const enrolled = await authService.isEnrolled(curso.id);
      if (enrolled) {
        navigate(`/curso/${curso.id}/estudo`);
      } else {
        navigate(courseUrl(curso));
      }
    }
  };

  const formatHours = (hours) => {
    return hours ? `Carga horária: ${hours} horas` : 'Carga horária não informada';
  };

  const displayCourseCount = '+400';

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  return (
    <div className="lista-cursos-page">
      {/* Hero Section - 3 columns */}
        <section className="hero-section">
          <div className="hero-grid">
            {/* Column 1: Text */}
            <div className="hero-col hero-text">
              <span className="hero-label">TURBINE SUA CARREIRA</span>
              <h1 className="hero-title">
                Cursos Online Gratuitos na <em>ABRALIBER</em>
              </h1>
              <p className="hero-subtitle">
                Mais de 400 cursos online para avançar no seu ritmo. Estude sem pagar
                e, depois de concluir, escolha seu certificado ou o Kit Carreira com currículo.
              </p>
              <ul className="hero-badges">
                <li>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="#6446d0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="10" stroke="#6446d0" strokeWidth="2"/></svg>
                  Estude 100% Grátis
                </li>
                <li>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="#6446d0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="10" stroke="#6446d0" strokeWidth="2"/></svg>
                  Cursos Rápidos
                </li>
                <li>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="#6446d0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="10" stroke="#6446d0" strokeWidth="2"/></svg>
                  Certificado e currículo opcionais
                </li>
              </ul>
              <div className="hero-cta">
                <a href="#cursos" className="hero-btn hero-btn-primary">Explorar cursos</a>
                <a href="/certificados" className="hero-btn hero-btn-secondary">Conhecer o Kit Carreira</a>
              </div>
            </div>

            {/* Column 2: Image Carousel */}
            <div className="hero-col hero-carousel">
              <div className="carousel-wrapper">
                <img 
                  src={currentSlide === 0 ? "/hero-person.webp" : "/hero-person2.webp"} 
                  alt="Estudante Abraliber" 
                  className="carousel-img" 
                  key={currentSlide}
                />
              </div>
            </div>

            {/* Column 3: Social Proof */}
            <div className="hero-col hero-social">
              <div className="hero-counter">
                <span className="hero-counter-number">1.139</span>
                <span className="hero-counter-label">alunos estão estudando agora</span>
              </div>
              <div className="hero-cta-badge">
                <img src="/pessoas.png" alt="Desenvolva suas habilidades e impulsione sua carreira" />
              </div>
              <div className="hero-testimonial">
                <p className="testimonial-text">"{testimonials[currentTestimonial].text}"</p>
                <div className="testimonial-author">
                  <img src={testimonials[currentTestimonial].photo} alt={testimonials[currentTestimonial].name} className="testimonial-photo" />
                  <span className="testimonial-name">{testimonials[currentTestimonial].name}</span>
                </div>
                <div className="testimonial-dots">
                  {testimonials.map((_, i) => (
                    <button key={i} className={`testimonial-dot${i === currentTestimonial ? ' active' : ''}`} onClick={() => setCurrentTestimonial(i)} aria-label={`Depoimento ${i + 1}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="stats-section">
          <div className="stats-container">
            <div className="stat-item">
              <span className="stat-number">{displayCourseCount}</span>
              <span className="stat-label">Cursos Disponíveis</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">+9.000</span>
              <span className="stat-label">Alunos Ativos</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">100%</span>
              <span className="stat-label">Cursos gratuitos</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">24h</span>
              <span className="stat-label">Acesso total</span>
            </div>
            <div className="stat-badge">
              <img src="/certificado-valido.png" alt="Certificação válida em todo o Brasil" className="stat-badge-img" />
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits-section">
          <div className="benefits-container">
            <div className="benefits-title-col">
              <h2 className="benefits-title">Benefícios para turbinar o seu futuro</h2>
            </div>
            <div className="benefits-cards">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><rect x="5" y="2" width="14" height="20" rx="2" stroke="#fff" strokeWidth="1.5"/><line x1="12" y1="18" x2="12" y2="18.01" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
                </div>
                <div className="benefit-text">
                  <h3>Estude de graça</h3>
                  <p>Você não paga nada para fazer os cursos. É só se matricular e começar a qualquer hora e lugar.</p>
                </div>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div className="benefit-text">
                  <h3>Currículo Profissional</h3>
                  <p>Crie, edite e baixe seu currículo com o curso concluído já incluído, sempre pronto para enviar às empresas.</p>
                </div>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><polyline points="14,2 14,8 20,8" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 15l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div className="benefit-text">
                  <h3>Certificado na hora</h3>
                  <p>Ao concluir seu curso, solicite o certificado digital de conclusão de forma rápida e sem burocracia.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Courses Section */}
      <section id="cursos" className="courses-container">
        <div className="section-header">
          <h2>Cursos Online Gratuitos</h2>
          <p className="section-subtitle">
            Descubra nossa ampla seleção de cursos em diversas áreas do conhecimento. 
            Todos os cursos são 100% gratuitos e você pode começar imediatamente.
          </p>
        </div>

        <SearchBar onFilter={handleFilter} />

        {categorias.length > 0 && (
          <div className="category-filter-section">
            <div className="category-filter-header">
              <strong>Categorias</strong>
              <span>{activeCategory?.nome || 'Todos os cursos'}</span>
            </div>
            <div className="category-chips-wrapper" role="group" aria-label="Filtrar cursos por categoria">
              <button
                className={`category-chip${!activeCategory ? ' active' : ''}`}
                onClick={() => setActiveCategory(null)}
                type="button"
                aria-pressed={!activeCategory}
              >
                Todos
              </button>
              {categorias.filter(c => c.cursos_count >= 3).map((cat) => (
                <button
                  key={cat.id}
                  className={`category-chip${activeCategory && activeCategory.id === cat.id ? ' active' : ''}`}
                  onClick={() => handleChipClick(cat)}
                  type="button"
                  aria-pressed={activeCategory?.id === cat.id}
                >
                  <span className="chip-dot" style={{ background: cat.cor }} />
                  {cat.nome}
                  <span className="chip-count">({cat.cursos_count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {cursos.length === 0 && !loading ? (
          <div className="loading-message">
            <p>{activeCategory ? `Nenhum curso encontrado na categoria "${activeCategory.nome}".` : 'Nenhum curso encontrado. Tente novamente mais tarde.'}</p>
          </div>
        ) : (
          <>
          <ul className="lista-cursos">
            {cursos.map((curso) => (
              <li key={curso.id}>
                <div className="curso-card">
                  <div className="curso-image-container">
                    <img 
                      src={curso.imagem_principal?.image_url || curso.imagem_url || getStorageUrl(curso.image) || '/default-course-image.svg'} 
                      alt={curso.title} 
                      className="curso-image"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/default-course-image.svg';
                      }}
                    />
                    <div className="curso-badge">100% Grátis</div>
                  </div>
                  
                  <div className="curso-content">
                    <h2>{truncateText(curso.title, 60)}</h2>
                    <p className="curso-description">
                      {truncateText(stripHtml(curso.description), 160)}
                    </p>
                    
                    <div className="curso-meta">
                      <span className="curso-hours">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                          <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {formatHours(curso.hours)}
                      </span>
                      <span className="curso-free">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Gratuito
                      </span>
                    </div>
                    
                    <button 
                      className="curso-button" 
                      onClick={() => handleMatriculaGratis(curso)}
                      data-analytics-course-id={curso.id}
                      data-analytics-source="home_catalog"
                      aria-label={`Fazer matrícula grátis no curso ${curso.title}`}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Matrícula Grátis
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {currentPage < lastPage && (
            <div className="load-more-container">
              <button 
                className="load-more-button" 
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <div className="load-more-spinner"></div>
                    Carregando...
                  </>
                ) : (
                  <>
                    Ver mais cursos
                    <span className="load-more-count">
                      ({cursos.length} de {totalCursos})
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
          </>
        )}

        {loading && (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        )}
      </section>
    </div>
  );
};

export default ListaCurso;
